import type { TokenUsage, UsageStage } from "../api/types";
import { fmtCost, fmtTokens, humanise, isVerifiedFree } from "../lib/format";
import { CostCell } from "./CostCell";

/**
 * Per-stage token table plus the document total -- "one by one, and overall".
 *
 * Every recorded stage is listed even when it consumed nothing, because a
 * zero-token stage that ran and a stage that never ran look identical once one
 * of them is omitted, and only one of those means the total is trustworthy.
 */
export function TokenBreakdown({ usage }: { usage: TokenUsage }) {
  const stages: UsageStage[] = usage.stages ?? [];

  if (!stages.length) {
    return (
      <div className="state">
        <strong>No token usage recorded</strong>
        Nothing was logged against this document. That is not the same as processing being free.
        {usage.note ? <div className="muted" style={{ marginTop: 8 }}>{usage.note}</div> : null}
      </div>
    );
  }

  return (
    <>
      {usage.note ? <div className="notice">{usage.note}</div> : null}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Stage</th>
              <th scope="col">Service</th>
              <th scope="col">Provider / model</th>
              <th scope="col" className="num">Calls</th>
              <th scope="col" className="num">Input</th>
              <th scope="col" className="num">Output</th>
              <th scope="col" className="num">Total</th>
              <th scope="col" className="num">Cost</th>
            </tr>
          </thead>
          <tbody>
            {stages.map((s, i) => (
              <tr key={`${s.service}-${s.stage}-${s.model ?? i}`}>
                <td>
                  {humanise(s.stage)}
                  {isVerifiedFree(s) ? <> <span className="pill">free</span></> : null}
                </td>
                <td className="muted">{s.service}</td>
                <td className="mono">
                  {s.provider ?? "—"}
                  {s.model ? <div className="muted">{s.model}</div> : null}
                </td>
                <td className="num">{fmtTokens(s.calls)}</td>
                <td className="num">{fmtTokens(s.input_tokens)}</td>
                <td className="num">{fmtTokens(s.output_tokens)}</td>
                <td className="num">{fmtTokens(s.total_tokens)}</td>
                <td className="num">
                  <CostCell usd={s.estimated_cost} verifiedFree={isVerifiedFree(s)} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row" colSpan={4}>Total</th>
              <td className="num"><strong>{fmtTokens(usage.total_input_tokens)}</strong></td>
              <td className="num"><strong>{fmtTokens(usage.total_output_tokens)}</strong></td>
              <td className="num"><strong>{fmtTokens(usage.total_tokens)}</strong></td>
              <td className="num">
                <strong>{fmtCost(usage.estimated_cost_usd)}</strong>
                {!usage.cost_complete ? (
                  <div className="muted" title="At least one stage has no price on file, so this total is a lower bound.">
                    partial
                  </div>
                ) : null}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}
